import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { API_URL } from '../config';
import { siteUploads } from '../api';
import { FaSpinner, FaCheckCircle, FaExclamationCircle, FaCamera, FaFileAlt } from 'react-icons/fa';

// ============================================
// PUBLIC SITE INSPECTION FORM
// Time-limited link, NO login required
// Uploads go to Supabase Storage (siteUploads)
// so images, PDFs AND videos persist on Render
// ============================================
function PublicSiteInspection() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [inspection, setInspection] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Brand green - used for inline styles so Tailwind can never purge it
  const BRAND_GREEN = '#00A86B';

  useEffect(() => {
    fetchInspection();
  }, [token]);

  const fetchInspection = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/site-inspections/public/${token}`);
      const data = await res.json();

      if (!data.success) {
        setError(data.error || 'Invalid inspection link');
        return;
      }

      if (data.expired || data.inspection?.status === 'expired') {
        setExpired(true);
        return;
      }

      if (data.inspection?.status === 'submitted') {
        setAlreadySubmitted(true);
        return;
      }

      setInspection(data.inspection);
      setQuestions(data.questions || []);

      const prefill = {};
      if (data.inspection?.quote?.contact_person) {
        prefill.site_contact = data.inspection.quote.contact_person;
      }
      setAnswers(prefill);
    } catch (err) {
      console.error('Error fetching inspection:', err);
      setError('Failed to load inspection form');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (key, value) => {
    setAnswers(prev => ({ ...prev, [key]: value }));
  };

  const uploadFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return [];

    const uploadedUrls = [];
    setUploading(true);

    try {
      for (const file of fileList) {
        // Use siteUploads (Supabase Storage) - supports images, PDFs AND videos up to 50MB
        const data = await siteUploads.upload(file);
        if (data.success && data.url) {
          uploadedUrls.push(data.url);
        } else {
          console.error('Upload failed:', data.error || 'Unknown error');
        }
      }
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }

    return uploadedUrls;
  };

  const handleFilesSelected = (e) => {
    const selected = Array.from(e.target.files || []);
    setFiles(prev => [...prev, ...selected]);
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    const unanswered = questions.filter(q => !answers[q.key]);
    if (unanswered.length > 0) {
      setError('Please answer all questions before submitting.');
      setSubmitting(false);
      return;
    }

    try {
      const uploadedUrls = await uploadFiles(files);

      const res = await fetch(`${API_URL}/site-inspections/public/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          answers,
          file_urls: uploadedUrls,
          contact_person: answers.site_contact || ''
        })
      });

      const data = await res.json();

      if (data.success) {
        setSuccess(true);
      } else {
        setError(data.error || 'Failed to submit inspection');
      }
    } catch (err) {
      console.error('Submit error:', err);
      setError('Failed to submit inspection. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ============================================
  // RENDER STATES
  // ============================================
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-green-500 mx-auto mb-4" />
          <p className="text-gray-500">Loading inspection form...</p>
        </div>
      </div>
    );
  }

  if (error && !inspection) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <FaExclamationCircle className="text-5xl text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Link Invalid</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <a href="/" className="inline-block bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors">
            Go to One Purpose Logistics
          </a>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <FaExclamationCircle className="text-5xl text-yellow-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Link Expired</h2>
          <p className="text-gray-500 mb-6">
            This inspection link has expired. Please contact One Purpose Logistics to request a new link.
          </p>
          <a href="/" className="inline-block bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors">
            Go to One Purpose Logistics
          </a>
        </div>
      </div>
    );
  }

  if (alreadySubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <FaCheckCircle className="text-5xl text-green-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Already Submitted</h2>
          <p className="text-gray-500 mb-6">
            This inspection has already been completed. Thank you!
          </p>
          <a href="/" className="inline-block bg-navy text-white px-6 py-2 rounded-lg hover:bg-green hover:text-navy transition-colors">
            Go to One Purpose Logistics
          </a>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <FaCheckCircle className="text-6xl text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-500 mb-2">
            Your site inspection details have been submitted successfully.
          </p>
          <p className="text-gray-400 text-sm mb-6">
            One Purpose Logistics will use this information to finalize your quotation.
          </p>
          <a href="/" className="inline-block bg-green text-white px-6 py-2 rounded-lg hover:bg-green transition-colors">
            Done
          </a>
        </div>
      </div>
    );
  }

  // ============================================
  // MAIN FORM
  // ============================================
  const quote = inspection?.quote;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="bg-navy text-white rounded-t-xl p-6">
          <h1 className="text-2xl font-bold mb-1">📋 Site Inspection</h1>
          <p className="text-sm opacity-80">One Purpose Logistics (Pty) Ltd</p>
        </div>

        <div className="bg-white border border-gray-200 border-t-0 p-6">
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div>
              <p className="text-xs text-gray-500">Quote Reference</p>
              <p className="font-mono font-bold text-navy">{quote?.quote_number || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Customer</p>
              <p className="font-semibold text-navy">{quote?.customer_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-xs text-gray-500">Type</p>
              <p className="font-semibold text-navy">{quote?.quote_type || 'N/A'}</p>
            </div>
            {quote?.tonnage && (
              <div>
                <p className="text-xs text-gray-500">Tonnage</p>
                <p className="font-semibold text-navy">{quote.tonnage} MT</p>
              </div>
            )}
          </div>

          {(quote?.origin || quote?.destination || quote?.description) && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
              {quote.origin && <p className="text-sm text-gray-700"><strong>From:</strong> {quote.origin}</p>}
              {quote.destination && <p className="text-sm text-gray-700"><strong>To:</strong> {quote.destination}</p>}
              {quote.description && (
                <p className="text-sm text-gray-700 mt-2"><strong>Details:</strong> {quote.description}</p>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="space-y-6">
              {questions.map((q) => (
                <div key={q.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {q.label}
                    <span className="text-red-500"> *</span>
                  </label>
                  {q.type === 'select' ? (
                    <div className="grid grid-cols-3 gap-2">
                      {q.options.map(opt => {
                        const isSelected = answers[q.key] === opt;
                        return (
                          <button
                            key={opt}
                            type="button"
                            onClick={() => handleAnswerChange(q.key, opt)}
                            className={`p-2 rounded-lg border-2 text-sm transition-all ${
                              isSelected ? 'text-white font-semibold' : 'border-gray-200 hover:border-gray-300 text-gray-700'
                            }`}
                            style={isSelected ? { borderColor: BRAND_GREEN, backgroundColor: BRAND_GREEN } : undefined}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <input
                      type="text"
                      value={answers[q.key] || ''}
                      onChange={(e) => handleAnswerChange(q.key, e.target.value)}
                      placeholder="Your answer..."
                      className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:border-green"
                      style={{ borderColor: '#E8ECEF' }}
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="mt-8">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                📎 Upload Site Photos / Documents / Videos{' '}
                <span className="text-xs text-gray-400">(optional, max 50MB per file)</span>
              </label>
              <div
                className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer hover:border-green-500 transition-colors"
                style={{ borderColor: '#E8ECEF' }}
                onClick={() => document.getElementById('inspection-files').click()}
              >
                <FaCamera className="text-3xl text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600">Click to upload photos, PDFs, or videos</p>
                <input
                  id="inspection-files"
                  type="file"
                  multiple
                  accept="image/*,application/pdf,video/*"
                  className="hidden"
                  onChange={handleFilesSelected}
                />
              </div>

              {files.length > 0 && (
                <div className="mt-3 space-y-2">
                  {files.map((file, index) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 rounded-lg p-2 px-3">
                      <span className="text-sm text-gray-700 flex items-center gap-2">
                        <FaFileAlt className="text-gray-400" size={14} />
                        {file.name}
                      </span>
                      <button type="button" onClick={() => removeFile(index)} className="text-red-500 text-sm hover:text-red-700">
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
                {error}
              </div>
            )}

            <div className="mt-8 pt-6 border-t border-gray-200">
              {/* 🔧 Fixed: inline brand green so it can never be white-on-white */}
              <button
                type="submit"
                disabled={submitting || uploading}
                className="w-full text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: BRAND_GREEN }}
              >
                {(submitting || uploading) && <FaSpinner className="animate-spin" />}
                {uploading ? 'Uploading files...' : submitting ? 'Submitting...' : 'Submit Site Inspection'}
              </button>
              <p className="text-xs text-gray-400 text-center mt-3">
                By submitting, you confirm the information provided is accurate.
              </p>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default PublicSiteInspection;